import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLearningAnalytics } from '../../../hooks/useLearningAnalytics';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, Cell,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import styles from './AnalyticsDashboard.module.css';
import type { LearningCurveData, SkillDistribution, RetentionRate, KnowledgeItem, WeakArea, TacticalThemeProficiency } from '../../../types/learning';
import VirtualizedList from '../../common/VirtualizedList';

// Import new chart components
import SequenceSkillProgressChart from './charts/SequenceSkillProgressChart';
import TacticalThemeRadarChart from './charts/TacticalThemeRadarChart';
import SkillCategoryBarChart from './charts/SkillCategoryBarChart';
import RecommendationsSection from './RecommendationsSection';

/**
 * AnalyticsDashboard
 *
 * Displays learning analytics using charts and summary data.
 * Fetches data via the useLearningAnalytics hook and renders charts using recharts.
 * Optimized with memoization and virtualized lists for better performance.
 * Styled to resemble a Cold War era intelligence report on greenbar computer paper.
 *
 * @module AnalyticsDashboard
 */
const COLORS = ['#1A3A63', '#4a7c59', '#BF9B30', '#D14836', '#555555'];

// Enhanced skeleton loading components
const SkeletonBar = () => <div className={styles.skeletonBar}></div>;
const SkeletonChart = () => (
  <div className={styles.skeletonChart}>
    <div className={styles.skeletonLines}>
      {[...Array(5)].map((_, i) => (
        <SkeletonBar key={i} />
      ))}
    </div>
  </div>
);

const SkeletonItem = () => (
  <div className={styles.skeletonItem}>
    <div className={styles.skeletonText}></div>
    <div className={styles.skeletonText} style={{ width: '40%' }}></div>
  </div>
);

// Enhanced loading state with skeleton UI
const LoadingState = () => (
  <div className={styles.dashboardContainer}>
    <div className={styles.leftFeedHoles}></div>
    <div className={styles.rightFeedHoles}></div>
    <div className={styles.topSecretStamp}>TOP SECRET</div>
    
    <h2>Intelligence Analysis Dashboard</h2>
    <div className={styles.loadingMessage}>Loading intelligence data...</div>
    
    <section className={styles.chartSection}>
      <h3>Memory Retention Curve</h3>
      <SkeletonChart />
    </section>
    
    <section className={styles.chartSection}>
      <h3>Agent Skill Distribution</h3>
      <SkeletonChart />
    </section>
    
    <section className={styles.chartSection}>
      <h3>Information Retention Assessment</h3>
      <div className={styles.retentionContainer}>
        <div className={styles.retentionStats}>
          <div><strong>Retention:</strong> <span className={styles.skeletonInline}></span></div>
          <div><strong>Total Reviews:</strong> <span className={styles.skeletonInline}></span></div>
          <div><strong>Correct Reviews:</strong> <span className={styles.skeletonInline}></span></div>
        </div>
        <div style={{ width: '400px', height: '250px' }}>
          <SkeletonChart />
        </div>
      </div>
    </section>
    
    <section className={styles.listSection}>
      <h3>Priority Intelligence Updates</h3>
      <div className={styles.skeletonList}>
        {[...Array(5)].map((_, i) => (
          <SkeletonItem key={i} />
        ))}
      </div>
    </section>
  </div>
);

const ErrorState = ({ message }: { message: string }) => <div role="alert">Error loading intelligence data: {message}</div>;
const EmptyState = () => <div>No intelligence data available at this time.</div>;

// DueItemsList as a separate memoized component
const DueItemsList = React.memo(({ items }: { items: KnowledgeItem[] }) => {
  if (items.length === 0) {
    return <div>All intelligence is up to date. No items pending review.</div>;
  }

  return (
    <VirtualizedList<KnowledgeItem>
      items={items}
      itemHeight={40}
      height={300}
      width="100%"
      renderItem={(item: KnowledgeItem, index: number) => (
        <div className={styles.dueItem}>
          <strong>{item.content}</strong>
          <span>(Next review: {new Date(item.nextReviewDate).toLocaleDateString()})</span>
        </div>
      )} 
    />
  );
});

// Add displayName to the memoized component
DueItemsList.displayName = 'DueItemsList';

const AnalyticsDashboard: React.FC = () => {
  const { loading, error, metrics, dueForReview } = useLearningAnalytics();
  const navigate = useNavigate();

  // Memoize data preparation for charts - moved up before conditional returns
  // Use empty arrays/objects as fallbacks when metrics might be null
  const learningCurveData = useMemo<LearningCurveData[]>(() => 
    (metrics?.learningCurve || []),
    [metrics?.learningCurve]
  );

  // Updated to handle the new SkillDistribution interface
  const skillDistData = useMemo(() => {
    // If no skill distribution data, return default empty categories
    if (!metrics?.skillDistribution || Object.keys(metrics.skillDistribution).length === 0) {
      return [
        { name: 'New', value: 0 },
        { name: 'Learning', value: 0 },
        { name: 'Mastered', value: 0 }
      ];
    }
    
    // Map skill categories into name/value pairs for the chart
    // Note: For backward compatibility, we're keeping the three traditional categories
    // but we could also just map all skill categories to show them all
    const distribution = metrics.skillDistribution;
    
    // Here we're extracting the key skills we want to specifically highlight
    // This can be adjusted based on naming conventions used in your app
    const newSkills = distribution['coordinate'] || 0;
    const learningSkills = distribution['move'] || 0; 
    const masteredSkills = distribution['sequence'] || 0;
    
    return [
      { name: 'New', value: newSkills },
      { name: 'Learning', value: learningSkills },
      { name: 'Mastered', value: masteredSkills }
    ];
  }, [metrics?.skillDistribution]);

  const retentionRate: RetentionRate = useMemo(() => 
    metrics?.retentionRate || { percentRetained: 0, totalReviews: 0, correctReviews: 0 },
    [metrics?.retentionRate]
  );

  // Memoize histogram data preparation
  const histogramData = useMemo(() => {
    const data = metrics?.gradeHistogramByType || [];
    // Get all unique types for legend/bars
    const allTypes: string[] = Array.from(new Set(data.map((d: { type: string }) => d.type)));
    // Group data by grade for recharts
    const grades = [0, 1, 2, 3, 4, 5];
    return grades.map((grade: number) => {
      const entry: Record<string, number | string> = { grade };
      allTypes.forEach((type: string) => {
        const found = data.find((d: { grade: number; type: string }) => d.grade === grade && d.type === type);
        entry[type] = found ? found.count : 0;
      });
      return entry;
    });
  }, [metrics?.gradeHistogramByType]);

  // Memoize type list for histogram
  const histogramTypes = useMemo(() => {
    const data = metrics?.gradeHistogramByType || [];
    return Array.from(new Set(data.map((d: { type: string }) => d.type)));
  }, [metrics?.gradeHistogramByType]);

  // NEW: Prepare skill category data for radar chart
  const skillCategoryData = useMemo(() => {
    if (!metrics?.skillDistribution || Object.keys(metrics.skillDistribution).length === 0) {
      return [];
    }
    
    return Object.entries(metrics.skillDistribution).map(([category, score]) => ({
      category,
      score,
      fullMark: 100
    }));
  }, [metrics?.skillDistribution]);

  // NEW: Prepare weak areas data for display
  const weakAreasData = useMemo(() => 
    metrics?.weakAreas || [],
    [metrics?.weakAreas]
  );

  // NEW: Prepare tactical themes data for bar chart
  const tacticalThemesData = useMemo(() => {
    if (!metrics?.tacticalThemeProficiency) {
      return [];
    }
    
    return Object.entries(metrics.tacticalThemeProficiency).map(([theme, score]) => ({
      theme,
      proficiency: score
    }));
  }, [metrics?.tacticalThemeProficiency]);

  // NEW: Prepare sequence learning curve data
  const sequenceLearningCurveData = useMemo<LearningCurveData[]>(() => 
    (metrics?.sequenceLearningCurve || []),
    [metrics?.sequenceLearningCurve]
  );

  // NEW: Prepare tactical theme data for Radar Chart
  const tacticalThemeDataForRadar = useMemo(() => {
    if (!metrics?.tacticalThemeProficiency) return [];
    return Object.entries(metrics.tacticalThemeProficiency).map(([theme, score]) => ({
      subject: theme.charAt(0).toUpperCase() + theme.slice(1), // Capitalize for display
      score: score,
      fullMark: 100, // Assuming proficiency is out of 100
    }));
  }, [metrics?.tacticalThemeProficiency]);

  // NEW: Prepare data for the SkillCategoryBarChart to show all skill categories
  const allSkillCategoriesChartData = useMemo(() => {
    if (!metrics?.skillDistribution || Object.keys(metrics.skillDistribution).length === 0) {
      return [];
    }
    return Object.entries(metrics.skillDistribution).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1), // Capitalize for display
      value,
    }));
  }, [metrics?.skillDistribution]);

  // Handler for "Practice Now" button
  const handlePracticeArea = (category: string) => {
    const categoryLower = category.toLowerCase();
    console.log(`Attempting to navigate to practice area for: ${categoryLower}`);

    if (categoryLower === 'coordinate' || categoryLower === 'coordinates') { // Handle plural form from some data
      navigate('/training'); // TrainingPage is currently hardcoded for CoordinateChallenge
    } else if (categoryLower === 'move' || categoryLower === 'moves') {
      // TODO: Implement robust navigation to a "move" specific training or mission.
      // For now, navigate to daily training, which might include move challenges, or a placeholder.
      console.warn(`Specific 'move' category practice path TBD. Navigating to daily training as a fallback.`);
      navigate('/training/daily'); 
    } else if (categoryLower === 'sequence' || categoryLower === 'sequences') {
      // TODO: Implement robust navigation to a "sequence" specific training or mission.
      // For now, navigate to daily training, which might include sequence challenges, or a placeholder.
      console.warn(`Specific 'sequence' category practice path TBD. Navigating to daily training as a fallback.`);
      navigate('/training/daily');
    } else {
      // Generic fallback for other/unknown categories
      console.warn(`Unknown or generic practice category: ${category}. Navigating to general training page.`);
      navigate('/training'); 
    }
  };

  // Early return for loading and error states - after hooks are called
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error.message} />;
  if (!metrics) return <EmptyState />;

  return (
    <div className={styles.dashboardContainer}>
      {/* Tractor feed holes */}
      <div className={styles.leftFeedHoles}></div>
      <div className={styles.rightFeedHoles}></div>
      
      {/* Title with TOP SECRET stamp right after the text */}
      <div style={{ marginBottom: '1.5rem', marginTop: '0.8rem' }}>
        <h2 style={{ display: 'inline', color: '#1A3A63' }}>Intelligence Analysis Dashboard</h2>
        <div className={styles.topSecretStamp}>TOP SECRET</div>
      </div>

      {/* Learning Curve Line Chart */}
      <section aria-labelledby="learning-curve-title" className={styles.chartSection}>
        <h3 id="learning-curve-title">Memory Retention Curve</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart 
            data={learningCurveData} 
            margin={{ top: 16, right: 32, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#AAA" />
            <XAxis dataKey="date" stroke="#333" reversed={true} />
            <YAxis stroke="#333" />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'rgba(248, 248, 248, 0.95)', 
                border: '1px solid #555',
                color: '#1a1a1a',
                boxShadow: '2px 2px 5px rgba(0, 0, 0, 0.2)'
              }} 
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="reviewed" 
              stroke={COLORS[0]} 
              strokeWidth={2}
              name="Information Reviewed" 
              dot={{ stroke: COLORS[0], strokeWidth: 1, fill: '#fff', r: 3 }}
              activeDot={{ stroke: COLORS[0], strokeWidth: 1, fill: COLORS[0], r: 5 }}
            />
            <Line 
              type="monotone" 
              dataKey="mastered" 
              stroke={COLORS[1]} 
              strokeWidth={2}
              name="Intelligence Mastered" 
              dot={{ stroke: COLORS[1], strokeWidth: 1, fill: '#fff', r: 3 }}
              activeDot={{ stroke: COLORS[1], strokeWidth: 1, fill: COLORS[1], r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </section>

      {/* NEW: Sequence Skill Progress Chart */}
      <section aria-labelledby="sequence-progress-title" className={styles.chartSection}>
        <h3 id="sequence-progress-title">Sequence Infiltration Analysis</h3>
        <SequenceSkillProgressChart data={sequenceLearningCurveData} />
      </section>

      {/* Skill Category Tracker with Radar Chart - Retitled */}
      <section aria-labelledby="skill-assessment-title" className={styles.chartSection}>
        <h3 id="skill-assessment-title">Operative Skill Assessment</h3>
        <div className={styles.skillCategoryContainer}>
          <div className={styles.radarChartContainer}>
            {skillCategoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <RadarChart 
                  cx="50%" 
                  cy="50%" 
                  outerRadius="80%" 
                  data={skillCategoryData}
                >
                  <PolarGrid />
                  <PolarAngleAxis dataKey="category" />
                  <PolarRadiusAxis domain={[0, 100]} />
                  <Radar 
                    name="Skill Level" 
                    dataKey="score" 
                    stroke={COLORS[0]} 
                    fill={COLORS[0]} 
                    fillOpacity={0.6} 
                  />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            ) : (
              <div className={styles.noDataMessage}>No skill category data available</div>
            )}
          </div>
        </div>
        
        {/* Personalized Recommendations Section (sole display for weak areas with actions) */}
        <RecommendationsSection weakAreas={weakAreasData} onPracticeArea={handlePracticeArea} />
      </section>

      {/* NEW: Tactical Theme Proficiency Radar Chart - Retitled */}
      <section aria-labelledby="tactical-radar-title" className={styles.chartSection}>
        <h3 id="tactical-radar-title">Tactical Acumen Profile</h3>
        <TacticalThemeRadarChart data={tacticalThemeDataForRadar} />
      </section>

      {/* Skill Distribution Bar Chart - Retitled to Overall Progress Stages */}
      <section aria-labelledby="progress-stages-title" className={styles.chartSection}>
        <h3 id="progress-stages-title">Overall Progress Stages</h3>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart 
            data={skillDistData} 
            margin={{ top: 16, right: 32, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#AAA" />
            <XAxis dataKey="name" stroke="#333" />
            <YAxis allowDecimals={false} stroke="#333" />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'rgba(248, 248, 248, 0.95)', 
                border: '1px solid #555',
                color: '#1a1a1a',
                boxShadow: '2px 2px 5px rgba(0, 0, 0, 0.2)'
              }} 
            />
            <Bar dataKey="value" fill="#8884d8">
              {skillDistData.map((entry, idx) => (
                <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </section>

      {/* NEW: Skill Category Comparison Bar Chart - Retitled */}
      <section aria-labelledby="category-comparison-title" className={styles.chartSection}>
        <h3 id="category-comparison-title">Operative Competency Matrix</h3>
        <SkillCategoryBarChart data={allSkillCategoriesChartData} />
      </section>

      {/* Retention Rate Summary and Grouped Histogram */}
      <section aria-labelledby="retention-title" className={styles.chartSection}>
        <h3 id="retention-title">Information Retention Assessment</h3>
        <div className={styles.retentionContainer}>
          <div className={styles.retentionStats}>
            <div><strong>Retention:</strong> {retentionRate.percentRetained}%</div>
            <div><strong>Total Reviews:</strong> {retentionRate.totalReviews}</div>
            <div><strong>Correct Reviews:</strong> {retentionRate.correctReviews}</div>
          </div>
          <ResponsiveContainer width={400} height={250}>
            <BarChart 
              data={histogramData} 
              margin={{ top: 16, right: 32, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#AAA" />
              <XAxis 
                dataKey="grade" 
                stroke="#333"
                label={{ 
                  value: 'Performance Grade', 
                  position: 'insideBottom', 
                  offset: -5,
                  fill: '#333'
                }} 
              />
              <YAxis 
                allowDecimals={false} 
                stroke="#333"
                label={{ 
                  value: 'Count', 
                  angle: -90, 
                  position: 'insideLeft',
                  fill: '#333'
                }} 
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(248, 248, 248, 0.95)', 
                  border: '1px solid #555',
                  color: '#1a1a1a',
                  boxShadow: '2px 2px 5px rgba(0, 0, 0, 0.2)'
                }} 
              />
              <Legend wrapperStyle={{margin: '-1rem 3rem'}} />
              {histogramTypes.map((type, idx) => (
                <Bar 
                  key={type} 
                  dataKey={type} 
                  name={type.charAt(0).toUpperCase() + type.slice(1)} 
                  fill={COLORS[idx % COLORS.length]} 
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Due for Review List */}
      <section aria-labelledby="due-review-title" className={styles.listSection}>
        <h3 id="due-review-title">Priority Intelligence Updates</h3>
        <DueItemsList items={dueForReview} />
      </section>
    </div>
  );
};

export default React.memo(AnalyticsDashboard); 